import {
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  DestroyRef,
  ElementRef,
  computed,
  effect,
  inject,
  input,
  signal,
  viewChild,
} from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import {
  ModusWcButton,
  ModusWcIcon,
  ModusWcTypography,
  ModusWcUtilityPanel,
} from '@trimble-oss/moduswebcomponents-angular';
import {
  CHAT_UI_URLS,
  ChatUiEventTypes,
  ChatUiVariants,
  ContentVariants,
  listenToChatUi,
  listenToChatUiEvents,
  updateConfig,
  type ChatUiConfiguration,
  type ChatUiEvent,
} from '@trimble-agentic-external-npm-local/agentic-platform-sdk-iframe-typescript';
import { AgenticChatService } from '../../services/agentic-chat/agentic-chat.service';
import type { AgenticChatUiVariant } from '../../interfaces/agentic-chat/agentic-chat-config.interface';
import { ThemeService } from '../../services/theme.service';
import { generateTestId } from '../../utils/test-identifier.utils';

const DESKTOP_PUSH_MEDIA = '(min-width: 768px)';
@Component({
  selector: 'app-agentic-chat-panel',
  standalone: true,
  imports: [ModusWcTypography, ModusWcButton, ModusWcIcon, ModusWcUtilityPanel],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  templateUrl: './agentic-chat-panel.component.html',
  styleUrl: './agentic-chat-panel.component.scss',
  host: {
    class: 'agentic-chat-panel',
    '[attr.aria-hidden]': 'isOpen() ? "false" : "true"',
  },
})
export class AgenticChatPanelComponent {
  /** Main workspace column the drawer pushes on wide viewports (Modus Agentic Chat Drawer). */
  readonly pushTarget = input.required<HTMLElement>();
  private readonly agenticChat = inject(AgenticChatService);

  private readonly themeService = inject(ThemeService);
  private readonly sanitizer = inject(DomSanitizer);
  private readonly destroyRef = inject(DestroyRef);
  private readonly chatFrame = viewChild<ElementRef<HTMLIFrameElement>>('chatFrame');
  private stopListen: (() => void) | undefined;
  private stopEvents: (() => void) | undefined;
  readonly isOpen = this.agenticChat.isOpen;
  readonly usePushContent = signal(this.readDesktopPushPreference());
  readonly chatUiUrl = computed<SafeResourceUrl>(() =>
    this.sanitizer.bypassSecurityTrustResourceUrl(CHAT_UI_URLS[this.agenticChat.environment])
  );
  readonly chatUiOrigin = computed(() => new URL(CHAT_UI_URLS[this.agenticChat.environment]).origin);
  private bridgeAttached = false;
  constructor() {
    this.bindPushContentMediaQuery();
    effect(() => {
      if (!this.isOpen()) {
        this.detachBridge();
        return;
      }

      const frame = this.chatFrame()?.nativeElement;

      if (!frame) {
        return;
      }
      this.attachBridge(frame);
    });

    effect(() => {

      const theme = this.themeService.currentTheme();

      if (!this.isOpen() || !this.bridgeAttached) {

        return;

      }

      const frame = this.chatFrame()?.nativeElement;

      if (!frame) {
        return;
      }

      updateConfig(frame, this.chatUiOrigin(), this.buildChatUiConfiguration(theme));

    });
    this.destroyRef.onDestroy(() => this.detachBridge());

  }

  getTestId(elementType: string, actionOrPurpose: string, qualifier?: string): string {
    return generateTestId('AgenticChatPanel', elementType, actionOrPurpose, qualifier);
  }

  closePanel(): void {
    this.agenticChat.close();
  }

  onPanelClosed(): void {
    if (this.isOpen()) {
      this.agenticChat.close();
    }
  }

  private readDesktopPushPreference(): boolean {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return true;
    }
    return window.matchMedia(DESKTOP_PUSH_MEDIA).matches;
  }

  private bindPushContentMediaQuery(): void {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return;
    }
    const media = window.matchMedia(DESKTOP_PUSH_MEDIA);
    const sync = (): void => this.usePushContent.set(media.matches);
    media.addEventListener('change', sync);
    this.destroyRef.onDestroy(() => media.removeEventListener('change', sync));
  }



  private mapUiVariant(variant: AgenticChatUiVariant): ChatUiVariants {
    switch (variant) {
      case 'full':
        return ChatUiVariants.Full;
      case 'minimal':
        return ChatUiVariants.Minimal;
      default:
        return ChatUiVariants.Narrow;
    }

  }

  private buildChatUiConfiguration(theme: 'light' | 'dark'): ChatUiConfiguration {

    return {
      environment: this.agenticChat.environment,
      agentId: this.agenticChat.agentId,
      uiConfig: {
        theme,
        contentVariant: ContentVariants.Chat,
        variant: this.mapUiVariant(this.agenticChat.uiVariant),
        chatInput: {
          buttons: [],
          hideModelSelection: this.agenticChat.hideModelSelection,
        },
        showSignIn: false,
        productAgents: [this.agenticChat.agentId],
      },
      localization: {
        locale: 'en-US',
      },
    };
  }

  private attachBridge(iframe: HTMLIFrameElement): void {

    this.detachBridge();
    const targetOrigin = this.chatUiOrigin();
    const provideConfig = (): ChatUiConfiguration =>
      this.buildChatUiConfiguration(this.themeService.currentTheme());
    const provideToken = (): Promise<string> => this.agenticChat.getAccessToken();

    this.stopListen = listenToChatUi(
      iframe,
      targetOrigin,
      provideConfig,
      provideToken,
      async () => ({ runContext: { context: [] }, tools: { runTime: {}, global: {} } }),
      provideToken
    );

    this.bridgeAttached = true;

    this.stopEvents = listenToChatUiEvents(targetOrigin, (event: ChatUiEvent) => {
      if (event.type === ChatUiEventTypes.OnClose) {
        this.agenticChat.close();

      }
    });
  }

  private detachBridge(): void {
    this.bridgeAttached = false;
    this.stopListen?.();
    this.stopListen = undefined;
    this.stopEvents?.();
    this.stopEvents = undefined;
  }
}